import styled from "@emotion/styled";
import { groupBy } from "es-toolkit";
import { useAtomValue } from "jotai";
import { useMemo, useState } from "react";

import {
  Droppable,
  FONT_WEIGHT_SEMI_BOLD,
  Icon,
  NAV_BAR_HEIGHT,
  TextField,
  showDndDebugInfo,
  transientOptions,
  zIndexes,
} from "@skilitics/designer-ui-components";
import { useFormatMessage } from "@skilitics/localization";

import { collapseThumbnailNavigationBar } from "../../application/applicationSettingsAtoms";
import type { ConditionBuildable, EffectBuildable } from "../../types";
import { TYPE_CANCEL } from "../constants";
import { ConditionDraggable } from "../dnd/ConditionDraggable";
import type { DroppableData } from "../dnd/dndSchemas";
import { EffectDraggable } from "../dnd/EffectDraggable";
import { DEBUGResponseDnd } from "../editors/DEBUGResponseDnd";
import { makeBuildableKey } from "../editors/utils";
import { layersPanelWidthAtom } from "./atoms";
import { SidebarList } from "./SidebarList";

const SearchIcon = styled(Icon)`
  margin: 0 0 0 10px;
`;

const ResponseSidebarContainer = styled("div", transientOptions)<{ $thumbnailNavigationBarCollapsed?: boolean }>`
  position: relative;
  z-index: ${zIndexes.triggersOverlaySidebar};
  overflow: hidden;

  background: ${({ theme }) => theme.surface};
  border-right: solid 1px ${({ theme }) => theme.outlineSoft};

  width: 100%;

  // note: to allow flow down to the bottom of the page
  height: ${({ $thumbnailNavigationBarCollapsed = false }) =>
    `calc(100% + ${$thumbnailNavigationBarCollapsed ? 0 : NAV_BAR_HEIGHT}px)`};
`;

const MenuContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: 100%;
`;

const MenuSection = styled.div`
  display: flex;
  flex-direction: column;
  position: relative;
  flex: 1 1 auto;
  width: 100%;
  overflow: hidden;
  min-height: 40%;

  &:not(:last-of-type) {
    border-bottom: solid 1px ${({ theme }) => theme.outline};
  }
`;

const MenuFilterContainer = styled.div`
  flex: 0 0 auto;
`;

const headerHeight = 40;
const MenuHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: ${headerHeight}px;
  line-height: ${headerHeight}px;
  flex: 0 0 ${headerHeight}px;
  color: ${({ theme }) => theme.generalTextIcon};
  font-size: ${({ theme }) => theme.fontSize};
  padding: 0 12px;
  font-weight: ${FONT_WEIGHT_SEMI_BOLD};
`;

const MenuHeadline = styled.div`
  font-weight: bold;
  margin-right: 10px;
`;

const FilterTextField = styled(TextField)`
  width: 100%;
  input {
    padding: 8px 8px;
    height: 25px;
  }
`;

const MenuScrollWrapper = styled.div`
  overflow-y: auto;
  overflow-x: hidden;
  padding: 0 12px 12px 12px;
`;

type Props = {
  conditionBuildables: ConditionBuildable[];
  effectBuildables: EffectBuildable[];
};

export const ResponseSidebar = ({ effectBuildables, conditionBuildables }: Props) => {
  // Menu Filters
  const [filterText, setFilterText] = useState("");

  const formatMessage = useFormatMessage(formatStrings);

  const layersPanelWidth = useAtomValue(layersPanelWidthAtom);

  const effectBuildablesByGroup = useMemo(() => {
    const filteredEffects = effectBuildables.filter(
      (eb) =>
        !filterText || (eb.spec?.title && (eb.spec?.title as string).toLowerCase().includes(filterText.toLowerCase())),
    );

    return groupBy(filteredEffects, (eb) => eb.spec?.category ?? eb.builder.category ?? formatMessage("other"));
  }, [effectBuildables, filterText, formatMessage]);

  const conditionBuildablesByGroup = useMemo(() => {
    const filteredConditions = conditionBuildables.filter(
      (cb) =>
        !filterText || (cb.spec?.title && (cb.spec?.title as string).toLowerCase().includes(filterText.toLowerCase())),
    );
    return groupBy(filteredConditions, (cb) => cb.spec?.category ?? cb.builder.defaultCategory);
  }, [filterText, conditionBuildables]);

  const thumbnailNavigationBarCollapsed = useAtomValue(collapseThumbnailNavigationBar);

  return (
    <ResponseSidebarContainer
      $thumbnailNavigationBarCollapsed={thumbnailNavigationBarCollapsed}
      style={{ width: layersPanelWidth }}
      data-test="conditions-and-effects-container"
    >
      {/* This is a placeholder dropzone for the menu so that the collision detection doesn't
                try to move draggable into closest eligible dropzone */}
      <Droppable
        id="menuCancelDragDropzone"
        data={{ rule: { type: TYPE_CANCEL } } satisfies DroppableData}
        hideBorder={true}
      >
        <MenuContainer>
          {showDndDebugInfo && (
            <MenuSection key="Debug" style={{ flexShrink: 0 }}>
              <DEBUGResponseDnd />
            </MenuSection>
          )}

          <MenuFilterContainer>
            <FilterTextField
              slotProps={{
                input: { startAdornment: <SearchIcon icon="search" size={10} /> },
              }}
              size="small"
              placeholder={formatMessage("filterPlaceholder")}
              value={filterText}
              onChange={(e) => setFilterText(e.target.value)}
              autoComplete="off"
            />
          </MenuFilterContainer>

          <MenuSection key="conditions" data-test="conditions-container">
            <MenuHeader>
              <MenuHeadline data-test="heading">{formatMessage("conditionsHeader")}</MenuHeadline>
            </MenuHeader>
            <MenuScrollWrapper>
              {Object.entries(conditionBuildablesByGroup).map(([group, buildables]) => (
                <SidebarList
                  key={group}
                  title={group}
                  items={buildables.map((buildable) => (
                    <ConditionDraggable key={makeBuildableKey(buildable)} conditionBuildable={buildable} />
                  ))}
                />
              ))}
            </MenuScrollWrapper>
          </MenuSection>

          <MenuSection key="effects" data-test="effects-container">
            <MenuHeader>
              <MenuHeadline data-test="heading">{formatMessage("effectsHeader")}</MenuHeadline>
            </MenuHeader>
            <MenuScrollWrapper>
              {Object.entries(effectBuildablesByGroup).map(([group, buildables]) => (
                <SidebarList
                  key={group}
                  title={group}
                  items={buildables.map((buildable) => (
                    <EffectDraggable key={makeBuildableKey(buildable)} effectBuildable={buildable} />
                  ))}
                />
              ))}
            </MenuScrollWrapper>
          </MenuSection>
        </MenuContainer>
      </Droppable>
    </ResponseSidebarContainer>
  );
};

const formatStrings = {
  conditionsHeader: {
    en: "Conditions",
  },
  effectsHeader: {
    en: "Actions",
  },
  filterPlaceholder: {
    en: "Filter conditions and actions",
  },
  other: {
    en: "Other",
  },
};
